import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import * as XLSX from 'xlsx';
import './SchoolDashboard.css';

function TransactionsPreview({
  pageTitle = 'Transactions Reports',
  pageSubtitle = 'View, filter, print, and export payment transaction reports',
  excelSheetName = 'Transactions',
  excelFilePrefix = 'Transactions',
  dateHeader = 'Transaction Date',
  detailTitle = 'Transaction Details'
}) {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  
  // Selectors
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'class-wise'
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Transactions
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // View transaction details
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);

  // Helper function to get base class name without section
  const getClassDisplayName = (className) => {
    if (!className) return 'No Class';
    if (className.includes(' - ')) {
      return className.split(' - ')[0].trim();
    }
    return className.trim();
  };

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    return parseFloat(num || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (!storedSchoolData) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(storedSchoolData));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchAcademicYears();
      fetchTerms();
      fetchClasses();
    }
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    if (schoolData?.id && selectedAcademicYear) {
      fetchTransactions();
    }
  }, [schoolData?.id, selectedBranchId, selectedAcademicYear, selectedTerm, selectedClass, viewMode, fromDate, toDate]);

  const fetchAcademicYears = async () => {
    try {
      const { data, error } = await supabase
        .from('academic_years')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('start_date', { ascending: false });

      if (error) {
        console.error('Error fetching academic years:', error);
        return;
      }

      setAcademicYears(data || []);
      if (data && data.length > 0 && !selectedAcademicYear) {
        setSelectedAcademicYear(data[0].id);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchTerms = async () => {
    try {
      const { data, error } = await supabase
        .from('terms')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('start_date', { ascending: true });

      if (error) {
        console.error('Error fetching terms:', error);
        return;
      }

      setTerms(data || []);
      if (data && data.length > 0 && !selectedTerm) {
        setSelectedTerm(data[0].id);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('class_name', { ascending: true });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      setClasses(data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchTransactions = async () => {
    if (!selectedAcademicYear) return;

    try {
      setLoadingTransactions(true);
      let query = supabase
        .from('student_payments')
        .select(`
          *,
          students:student_id (
            id,
            first_name,
            middle_name,
            last_name,
            student_id_no,
            class_id,
            classes:class_id (id, class_name)
          )
        `)
        .eq('school_id', schoolData.id)
        .eq('academic_year_id', selectedAcademicYear)
        .order('payment_date', { ascending: false });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      if (selectedTerm) {
        const termName = terms.find(t => t.id === parseInt(selectedTerm))?.name || selectedTerm;
        query = query.eq('term', termName);
      }

      // Date range filter
      if (fromDate) {
        query = query.gte('payment_date', fromDate);
      }
      if (toDate) {
        query = query.lte('payment_date', toDate);
      }

      // Class filter
      if (viewMode === 'class-wise' && selectedClass) {
        // Filter by students in the selected class
        const { data: classStudents } = await supabase
          .from('students')
          .select('id')
          .eq('class_id', selectedClass)
          .eq('is_active', true);
        
        if (classStudents && classStudents.length > 0) {
          const studentIds = classStudents.map(s => s.id);
          query = query.in('student_id', studentIds);
        } else {
          setTransactions([]);
          setLoadingTransactions(false);
          return;
        }
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching transactions:', error);
        setTransactions([]);
        return;
      }

      setTransactions(data || []);
    } catch (error) {
      console.error('Error:', error);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionModal(true);
  };

  const handleDownloadExcel = () => {
    if (transactions.length === 0) {
      alert('No transactions to download.');
      return;
    }

    // Prepare data for Excel
    const excelData = transactions.map(transaction => {
      const student = transaction.students;
      const studentName = `${student?.first_name || ''} ${student?.middle_name || ''} ${student?.last_name || ''}`.trim();
      
      return {
        [dateHeader]: new Date(transaction.payment_date).toLocaleDateString(),
        'Receipt No.': transaction.receipt_number,
        'Student Name': studentName,
        'Admission No.': student?.student_id_no || 'N/A',
        'Class': getClassDisplayName(student?.classes?.class_name || ''),
        'Payment For': transaction.payment_for,
        'Payment Method': transaction.payment_method,
        'Amount Paid': parseFloat(transaction.amount_paid || 0),
        'Discount Applied': parseFloat(transaction.discount_applied || 0),
        'Net Amount': parseFloat(transaction.amount_paid || 0) - parseFloat(transaction.discount_applied || 0),
        'Reference Number': transaction.reference_number || 'N/A',
        'Term': transaction.term || 'N/A',
        'Notes': transaction.notes || ''
      };
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, excelSheetName);

    // Set column widths
    const colWidths = [
      { wch: 15 }, // Transaction Date
      { wch: 20 }, // Receipt No.
      { wch: 25 }, // Student Name
      { wch: 15 }, // Admission No.
      { wch: 15 }, // Class
      { wch: 25 }, // Payment For
      { wch: 15 }, // Payment Method
      { wch: 12 }, // Amount Paid
      { wch: 15 }, // Discount Applied
      { wch: 12 }, // Net Amount
      { wch: 20 }, // Reference Number
      { wch: 10 }, // Term
      { wch: 30 }  // Notes
    ];
    ws['!cols'] = colWidths;

    // Generate filename
    const academicYear = academicYears.find(ay => ay.id === parseInt(selectedAcademicYear));
    const termName = terms.find(t => t.id === parseInt(selectedTerm))?.name || 'All Terms';
    const dateRange = fromDate && toDate 
      ? `${new Date(fromDate).toLocaleDateString()}_to_${new Date(toDate).toLocaleDateString()}`
      : 'All_Dates';
    const filename = `${excelFilePrefix}_${academicYear?.year_name || 'All'}_${termName}_${dateRange}.xlsx`;

    // Download
    XLSX.writeFile(wb, filename);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />
      
      <SchoolSidebar schoolData={schoolData} />
      
      <div className="main-content" style={{ 
        width: '100%', 
        maxWidth: '100%', 
        overflowX: 'hidden',
        boxSizing: 'border-box',
        padding: '1.5rem'
      }}>
        <div className="content-header" style={{ 
          width: '100%', 
          maxWidth: '100%',
          boxSizing: 'border-box',
          marginBottom: '1rem'
        }}>
          <h1>{pageTitle}</h1>
          <p>{pageSubtitle}</p>
        </div>

        {/* Selectors Row */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '1rem', 
          marginBottom: '1.5rem',
          alignItems: 'flex-end',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <div className="form-group" style={{ minWidth: '200px', flex: '1' }}>
            <label htmlFor="academic_year">Academic Year *</label>
            <select
              id="academic_year"
              value={selectedAcademicYear}
              onChange={(e) => setSelectedAcademicYear(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="">Select Academic Year</option>
              {academicYears.map(year => (
                <option key={year.id} value={year.id}>
                  {year.year_name || `${new Date(year.start_date).getFullYear()}-${new Date(year.end_date).getFullYear()}`}
                </option>
              ))}
            </select>
          </div>

          <BranchSelector
            schoolId={schoolData?.id}
            selectedBranchId={selectedBranchId}
            onBranchChange={handleBranchChange}
          />

          <div className="form-group" style={{ minWidth: '150px', flex: '1' }}>
            <label htmlFor="term">Term</label>
            <select
              id="term"
              value={selectedTerm}
              onChange={(e) => setSelectedTerm(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="">All Terms</option>
              {terms.map(term => (
                <option key={term.id} value={term.id}>{term.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* View Mode and Class Selector */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '1rem', 
          marginBottom: '1.5rem',
          alignItems: 'flex-end',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <div className="form-group" style={{ minWidth: '150px' }}>
            <label>View Mode</label>
            <select
              value={viewMode}
              onChange={(e) => {
                setViewMode(e.target.value);
                if (e.target.value === 'all') {
                  setSelectedClass('');
                }
              }}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="all">All Classes</option>
              <option value="class-wise">Class-wise</option>
            </select>
          </div>

          {viewMode === 'class-wise' && (
            <div className="form-group" style={{ minWidth: '200px', flex: '1' }}>
              <label htmlFor="class">Class *</label>
              <select
                id="class"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
              >
                <option value="">Select Class</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {getClassDisplayName(cls.class_name)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Date Range Selector */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '1rem', 
          marginBottom: '1.5rem',
          alignItems: 'flex-end',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <div className="form-group" style={{ minWidth: '200px' }}>
            <label htmlFor="from_date">From Date</label>
            <input
              type="date"
              id="from_date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            />
          </div>

          <div className="form-group" style={{ minWidth: '200px' }}>
            <label htmlFor="to_date">To Date</label>
            <input
              type="date"
              id="to_date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            />
          </div>

          <div style={{ flex: '1', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <button
              onClick={handleDownloadExcel}
              disabled={transactions.length === 0}
              style={{
                padding: '0.75rem 1.5rem',
                background: transactions.length === 0 
                  ? '#D1D5DB' 
                  : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: transactions.length === 0 ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: transactions.length === 0 
                  ? 'none' 
                  : '0 4px 12px rgba(16, 185, 129, 0.3)',
                opacity: transactions.length === 0 ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (transactions.length > 0) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (transactions.length > 0) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                }
              }}
            >
              📥 Download to EXCEL
            </button>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="dashboard-section" style={{ 
          width: '100%', 
          maxWidth: '100%',
          boxSizing: 'border-box',
          padding: '2rem'
        }}>
          {loadingTransactions ? (
            <p style={{ textAlign: 'center', padding: '2rem' }}>Loading transactions...</p>
          ) : transactions.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
              No transactions found for the selected filters.
            </p>
          ) : (
            <div className="table-container" style={{
              width: '100%',
              maxWidth: '100%',
              overflowX: 'auto',
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e9ecef'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.9rem',
                minWidth: '1000px'
              }}>
                <thead>
                  <tr style={{ background: '#F9FAFB' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #E5E7EB', fontWeight: '600' }}>{dateHeader}</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #E5E7EB', fontWeight: '600' }}>Receipt No.</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #E5E7EB', fontWeight: '600' }}>Student Name</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #E5E7EB', fontWeight: '600' }}>Admission No.</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #E5E7EB', fontWeight: '600' }}>Class</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #E5E7EB', fontWeight: '600' }}>Amount</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #E5E7EB', fontWeight: '600' }}>Reference Number</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #E5E7EB', fontWeight: '600' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => {
                    const student = transaction.students;
                    const studentName = `${student?.first_name || ''} ${student?.middle_name || ''} ${student?.last_name || ''}`.trim();
                    const netAmount = parseFloat(transaction.amount_paid || 0) - parseFloat(transaction.discount_applied || 0);
                    
                    return (
                      <tr key={transaction.id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                        <td style={{ padding: '0.75rem', border: '1px solid #E5E7EB' }}>
                          {new Date(transaction.payment_date).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '0.75rem', border: '1px solid #E5E7EB', fontWeight: '600' }}>
                          {transaction.receipt_number}
                        </td>
                        <td style={{ padding: '0.75rem', border: '1px solid #E5E7EB' }}>{studentName}</td>
                        <td style={{ padding: '0.75rem', border: '1px solid #E5E7EB' }}>
                          {student?.student_id_no || 'N/A'}
                        </td>
                        <td style={{ padding: '0.75rem', border: '1px solid #E5E7EB' }}>
                          {getClassDisplayName(student?.classes?.class_name || '')}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #E5E7EB', fontWeight: '600', color: '#10B981' }}>
                          Rs {formatNumber(netAmount)}
                        </td>
                        <td style={{ padding: '0.75rem', border: '1px solid #E5E7EB' }}>
                          {transaction.reference_number || 'N/A'}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #E5E7EB' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleViewTransaction(transaction)}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#3B82F6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#2563EB';
                                e.target.style.transform = 'translateY(-1px)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = '#3B82F6';
                                e.target.style.transform = 'translateY(0)';
                              }}
                            >
                              👁️ View
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Transaction Details Modal */}
      {showTransactionModal && selectedTransaction && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            position: 'relative'
          }}>
            {/* Close Button */}
            <button
              onClick={() => {
                setShowTransactionModal(false);
                setSelectedTransaction(null);
              }}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: '#EF4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '1.2rem',
                fontWeight: 'bold'
              }}
            >
              ×
            </button>

            <h2 style={{ marginBottom: '1.5rem', color: '#1F2937' }}>{detailTitle}</h2>
            
            {(() => {
              const student = selectedTransaction.students;
              const studentName = `${student?.first_name || ''} ${student?.middle_name || ''} ${student?.last_name || ''}`.trim();
              const netAmount = parseFloat(selectedTransaction.amount_paid || 0) - parseFloat(selectedTransaction.discount_applied || 0);
              
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: '500' }}>{dateHeader}</label>
                      <p style={{ color: '#1F2937', fontWeight: '600' }}>
                        {new Date(selectedTransaction.payment_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <label style={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: '500' }}>Receipt Number</label>
                      <p style={{ color: '#1F2937', fontWeight: '600' }}>{selectedTransaction.receipt_number}</p>
                    </div>
                    <div>
                      <label style={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: '500' }}>Student Name</label>
                      <p style={{ color: '#1F2937', fontWeight: '600' }}>{studentName}</p>
                    </div>
                    <div>
                      <label style={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: '500' }}>Admission No.</label>
                      <p style={{ color: '#1F2937', fontWeight: '600' }}>{student?.student_id_no || 'N/A'}</p>
                    </div>
                    <div>
                      <label style={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: '500' }}>Class</label>
                      <p style={{ color: '#1F2937', fontWeight: '600' }}>
                        {getClassDisplayName(student?.classes?.class_name || '')}
                      </p>
                    </div>
                    <div>
                      <label style={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: '500' }}>Term</label>
                      <p style={{ color: '#1F2937', fontWeight: '600' }}>{selectedTransaction.term || 'N/A'}</p>
                    </div>
                    <div>
                      <label style={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: '500' }}>Payment For</label>
                      <p style={{ color: '#1F2937', fontWeight: '600' }}>{selectedTransaction.payment_for}</p>
                    </div>
                    <div>
                      <label style={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: '500' }}>Payment Method</label>
                      <p style={{ color: '#1F2937', fontWeight: '600' }}>{selectedTransaction.payment_method}</p>
                    </div>
                    <div>
                      <label style={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: '500' }}>Amount Paid</label>
                      <p style={{ color: '#10B981', fontWeight: '700', fontSize: '1.1rem' }}>
                        Rs {formatNumber(selectedTransaction.amount_paid)}
                      </p>
                    </div>
                    <div>
                      <label style={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: '500' }}>Discount Applied</label>
                      <p style={{ color: '#EF4444', fontWeight: '600' }}>
                        Rs {formatNumber(selectedTransaction.discount_applied || 0)}
                      </p>
                    </div>
                    <div>
                      <label style={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: '500' }}>Net Amount</label>
                      <p style={{ color: '#10B981', fontWeight: '700', fontSize: '1.2rem' }}>
                        Rs {formatNumber(netAmount)}
                      </p>
                    </div>
                    <div>
                      <label style={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: '500' }}>Reference Number</label>
                      <p style={{ color: '#1F2937', fontWeight: '600' }}>
                        {selectedTransaction.reference_number || 'N/A'}
                      </p>
                    </div>
                  </div>
                  
                  {selectedTransaction.notes && (
                    <div>
                      <label style={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: '500' }}>Notes</label>
                      <p style={{ color: '#1F2937' }}>{selectedTransaction.notes}</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Add CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default TransactionsPreview;
